import {
  ArrowLeftOutlined,
  InfoCircleOutlined,
  EditOutlined,
  TeamOutlined,
  AppstoreOutlined,
  BarsOutlined,
  LaptopOutlined,
  NumberOutlined,
  OrderedListOutlined,
  SortAscendingOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import { useAppSelector } from "../../../../store/hooks";
import { getRestaurantName } from "../../../../lib/restaurant";
import ProfileDropdown from "../../../../components/ProfileDropdown";
import { KbdChip } from "../modalPrimitives";
import { useOrderingLogout } from "../useOrderingLogout";
import type { CatalogSort, CatalogView } from "../../../../api/configuration";
import {
  useConfiguration,
  useUpdateUserConfiguration,
} from "../../../../hooks/useConfiguration";
import { nextCatalogSort, useCatalogSortOptions } from "./catalogSort";
import { SegmentedToggle } from "../SegmentedToggle";

interface OrderFooterProps {
  onBack?: () => void;
  onInfo?: () => void;
  onModify?: () => void;
  hasSelectedItem?: boolean;
  /** „Промени" редактира маркирания ред на избрания продукт — активен само
   *  когато избраният продукт вече е в поръчката. */
  canModify?: boolean;
  /** „Клиенти" (същият модал като на екрана с масите) — подава се само с
   *  правото select_customer; undefined = бутонът не се рендира. */
  onClients?: () => void;
  /** true само когато няма нито сметка, нито нова маса в процес на откриване
   *  (при нова маса изборът изчаква и пътува с CreateBillIn). */
  clientsDisabled?: boolean;
  /** Изглед на каталога — плочки (грид) / таблица (лист); F7 го обръща. */
  catalogView: CatalogView;
  onCatalogViewChange: (view: CatalogView) => void;
  /** Екранната клавиатура (F8). */
  keyboardOpen: boolean;
  onToggleKeyboard: () => void;
  /** Името на масата — стои вдясно до името на заведението (широк екран). */
  tableName?: string;
  /** Вместо „Маса …" — собствен надпис на мястото (напр. „Създаване на обедно меню"). */
  contextTitle?: string;
}

interface BtnProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  shortcut?: string;
  active?: boolean;
  /** Подсказка при заключено действие (локален режим, docs/edge.md). */
  lockedTooltip?: string;
}

/** Ghost бутон от долната лента (бял, с pos-ctl рамка и kbd chip горе-дясно);
 *  active = маркиран (акцентен фон), за превключвателите. Под 1440px
 *  (isWide) е по-стегнат, за да се събере целият футър на 1280. */
const Btn = ({
  icon,
  label,
  onClick,
  disabled,
  shortcut,
  active,
  lockedTooltip,
}: BtnProps) => {
  const { isWide } = useViewport();
  const button = (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || !!lockedTooltip}
      aria-pressed={active}
      className={`relative inline-flex h-11 items-center justify-center gap-1.5 rounded-[11px] border font-semibold whitespace-nowrap shrink-0 transition-colors cursor-pointer disabled:opacity-45 disabled:cursor-not-allowed ${
        lockedTooltip ? "pointer-events-none" : ""
      } ${
        isWide ? "px-4 text-[14px]" : "px-3 text-[13px]"
      } ${
        active
          ? "border-pos-accent bg-pos-accent text-white"
          : "border-pos-ctl bg-pos-card text-pos-ink2 hover:bg-pos-panel"
      }`}
    >
      <span
        className={`inline-flex items-center justify-center text-[14px] ${
          active ? "text-white/80" : "text-pos-dim"
        }`}
      >
        {icon}
      </span>
      <span>{label}</span>
      {shortcut && (
        <KbdChip
          shortcut={shortcut}
          colorClass={active ? "text-white/60" : "text-pos-faint"}
        />
      )}
    </button>
  );
  // Обвивката носи подсказката на заключено действие: disabled бутон не
  // получава mouse събития, затова той е pointer-events-none.
  return lockedTooltip ? (
    <Tooltip title={lockedTooltip}>
      <span className="inline-flex shrink-0 cursor-not-allowed">{button}</span>
    </Tooltip>
  ) : (
    button
  );
};

// Иконата и надписът на бутона за подредба — показват ТЕКУЩАТА подредба
const SORT_ICONS: Record<CatalogSort, React.ReactNode> = {
  alphabetical: <SortAscendingOutlined />,
  plu: <NumberOutlined />,
  sort_index: <OrderedListOutlined />,
};
const SORT_LABEL_KEYS: Record<CatalogSort, string> = {
  alphabetical: "ordering.sortName",
  plu: "ordering.sortPlu",
  sort_index: "ordering.sortIndex",
};

/**
 * Долна лента на изглед „Продукти" (само десктоп; горната лента тук няма).
 * Вляво: Назад (Esc) / Инфо / Промени (F9) / Клиенти. В средата: Плочки |
 * Таблица (F7), Клавиатура (F8) и подредбата АБВ → КОД → СОРТ ИНДЕКС (личната
 * настройка catalogSort, записва се директно; КОД/СОРТ ИНДЕКС само ако има PLU
 * кодове/сорт индекси — `useCatalogSortOptions`).
 * Вдясно: маса · заведение (широк екран),
 * Backoffice, цял екран и профилът (ProfileDropdown — същият като в горната
 * лента на масите). „Поръчай" живее в панела „Преглед на поръчката".
 */
export const OrderFooter = ({
  onBack,
  onInfo,
  onModify,
  hasSelectedItem = false,
  canModify = false,
  onClients,
  clientsDisabled = false,
  catalogView,
  onCatalogViewChange,
  keyboardOpen,
  onToggleKeyboard,
  tableName,
  contextTitle,
}: OrderFooterProps) => {
  const { t } = useTranslation();
  const { isWide } = useViewport();
  const names = useAppSelector((s) => s.auth.names);
  const username = useAppSelector((s) => s.auth.username);
  const access = useAppSelector((s) => s.auth.access);
  const displayName = names || username || "";
  const restaurantName = getRestaurantName();
  const { handleLogout, logoutModal } = useOrderingLogout();
  const { configuration } = useConfiguration();
  const { mutate: updateUserConfiguration } = useUpdateUserConfiguration();
  const sortOptions = useCatalogSortOptions();
  const catalogSort = configuration.catalogSort;

  return (
    <div
      className={`w-full bg-pos-panel border-t border-pos-line flex items-center py-2.5 ${
        isWide ? "gap-2.5 px-5" : "gap-1.5 px-3.5"
      }`}
    >
      <Btn
        icon={<ArrowLeftOutlined />}
        label={t("common.back")}
        onClick={onBack}
        shortcut="Esc"
      />
      {/* Инфо/Промени са на маркирането — редакторът на обедно меню ги няма */}
      {onInfo && (
        <Btn
          icon={<InfoCircleOutlined />}
          label={t("ordering.info")}
          onClick={onInfo}
          disabled={!hasSelectedItem}
        />
      )}
      {onModify && (
        <Btn
          icon={<EditOutlined />}
          label={t("ordering.modify")}
          onClick={onModify}
          disabled={!canModify}
          shortcut="F9"
        />
      )}
      {onClients && (
        <Btn
          icon={<TeamOutlined />}
          label={t("ordering.clients")}
          onClick={onClients}
          disabled={clientsDisabled}
        />
      )}

      <span aria-hidden className="w-px h-7 bg-pos-line shrink-0 mx-0.5" />

      {/* Плочки | Таблица — сегментиран превключвател на изгледа (F7 го обръща) */}
      <SegmentedToggle
        options={[
          {
            value: "grid",
            icon: <AppstoreOutlined className="text-[15px]" />,
            label: t("ordering.viewTiles"),
          },
          {
            value: "list",
            icon: <BarsOutlined className="text-[15px]" />,
            label: t("ordering.viewTable"),
          },
        ]}
        value={catalogView}
        onChange={onCatalogViewChange}
        shortcut="F7"
      />

      <Btn
        icon={<LaptopOutlined />}
        label={t("ordering.keyboard")}
        onClick={onToggleKeyboard}
        shortcut="F8"
        active={keyboardOpen}
      />

      {/* Подредба на каталога АБВ → КОД → СОРТ ИНДЕКС — само когато има поне още
          една освен „АБВ“ (без PLU кодове и сорт индекси всички са еднакви) */}
      {sortOptions.length > 1 && (
        <Btn
          icon={SORT_ICONS[catalogSort]}
          label={t(SORT_LABEL_KEYS[catalogSort])}
          onClick={() =>
            updateUserConfiguration({
              catalogSort: nextCatalogSort(catalogSort, sortOptions),
            })
          }
        />
      )}

      {/* Дясно: маса · заведение (широк екран) | Backoffice · цял екран · профил */}
      <div className="ml-auto flex items-center gap-3 min-w-0">
        {isWide && (contextTitle || tableName || restaurantName) && (
          <>
            <span className="min-w-0 truncate text-[13px] text-pos-soft">
              {contextTitle ? (
                <span className="font-bold text-pos-ink">{contextTitle}</span>
              ) : (
                tableName && (
                  <span className="font-bold text-pos-ink">
                    {t("closeTable.table")} {tableName}
                  </span>
                )
              )}
              {(contextTitle || tableName) && restaurantName && (
                <span className="text-pos-muted"> · </span>
              )}
              {restaurantName}
            </span>
            <span aria-hidden className="w-px h-8 bg-pos-line shrink-0" />
          </>
        )}
        {/* ProfileDropdown носи Backoffice линка, „цял екран" и името/аватара —
            същият компонент като в горната лента на масите */}
        <ProfileDropdown
          displayName={displayName}
          username={username ?? ""}
          access={access}
          isNarrow={false}
          onLogout={handleLogout}
        />
      </div>
      {logoutModal}
    </div>
  );
};
